import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input, Space, Typography, Alert, Descriptions, message } from 'antd';
import {
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
} from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  buildRemoteSwitchHex,
  adminRemoteRead51,
} from '../utils/yjMeterRemote';
import { isByManufacturer } from '../utils/deviceMeter';

const { Text } = Typography;
const { TextArea } = Input;

const formatVendorLog = (title, payload) => {
  let body = payload;
  if (typeof payload !== 'string') {
    try {
      body = JSON.stringify(payload, null, 2);
    } catch (e) {
      body = String(payload);
    }
  }
  return `===== ${title} =====\n时间: ${new Date().toISOString()}\n\n${body}`;
};

const listStatusLabel = (device) => {
  const name = String(device?.device_status_name || '').trim();
  if (name) return name;
  const s = Number(device?.device_status);
  if (s === 1) return '开闸';
  if (s === 0) return '关闸';
  return '-';
};

const fmtKwh = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '';
  return `${Number(v).toFixed(3)} 度`;
};

/**
 * 4G 电表：查询开关闸状态 / 开闸 / 关闸（普通开关闸，不写强制关闸标记）
 */
const MeterSwitchModal = ({ open, device, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState('');
  const [error, setError] = useState('');
  const [switchStatus, setSwitchStatus] = useState('-');
  const [remainText, setRemainText] = useState('-');
  const [totalText, setTotalText] = useState('-');
  const [byVendorLog, setByVendorLog] = useState('');
  const liveQueriedRef = useRef(false);
  const deviceIdRef = useRef(null);
  const isBYElectric = isByManufacturer(device);

  useEffect(() => {
    if (!open) return;
    const id = device?.id ?? null;
    if (deviceIdRef.current !== id) {
      deviceIdRef.current = id;
      liveQueriedRef.current = false;
      setLoading(false);
      setHint('');
      setError('');
      setRemainText('-');
      setTotalText('-');
      setByVendorLog('');
      setSwitchStatus(listStatusLabel(device) || '-');
    } else if (!liveQueriedRef.current) {
      setSwitchStatus(listStatusLabel(device) || '-');
    }
  }, [open, device?.id, device]);

  const appendByLog = (title, payload) => {
    const chunk = formatVendorLog(title, payload);
    setByVendorLog((prev) => (prev ? `${chunk}\n\n${prev}` : chunk));
  };

  const publishCmd = async (action) => {
    const labelMap = { open: '开闸', close: '关闸' };
    const label = labelMap[action] || '操作';
    if (!device) return;
    if (isBYElectric) {
      appendByLog(`远程${label}（预览）`, {
        note: action === 'open' ? '待接 openMeter + 轮询 OPEN' : '待接 closeMeter + 轮询 CLOSE',
        device_id: device.id,
        want_open: action === 'open',
      });
      message.info(`BY 远程${label}待接厂家 API（已写入下方完整返回区）`);
      return;
    }

    setLoading(true);
    setError('');
    setHint(`正在下发${label}指令…`);
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const payload = buildFinalPayload(
        buildRemoteSwitchHex(publishKey, action, isBleDirect),
        true
      );
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      liveQueriedRef.current = true;
      setSwitchStatus(action === 'open' ? '通电（已下发）' : '断电（已下发）');
      setHint(`${label}指令已发送，可用「获取开关闸状态」核对`);
      message.success(`${label}指令已发送`);
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || `${label}失败`;
      setError(errMsg);
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleQueryStatus = async () => {
    if (!device) return;
    if (isBYElectric) {
      appendByLog('获取开关闸状态·远程抄表（预览）', {
        note: '待接 readMeter + 轮询 READ，从结果取 jdqzt / 剩余 / 总量',
        device_id: device.id,
      });
      message.info('BY 远程抄表（含闸位）待接厂家 API（已写入下方完整返回区）');
      return;
    }
    setLoading(true);
    setError('');
    setHint('正在抄表获取开关闸状态…');
    try {
      const parsed = await adminRemoteRead51({
        device,
        getMqttMappingByMeter,
        mqttPublishByMeter,
        getMqttLatestMessage,
      });
      if (!parsed) {
        throw new Error('已下发抄表，但超时未收到回包，请稍后重试');
      }
      const sw = String(parsed.switchStatus || '').trim();
      liveQueriedRef.current = true;
      if (parsed.remain != null) setRemainText(fmtKwh(parsed.remain) || '-');
      if (parsed.total != null) setTotalText(fmtKwh(parsed.total) || '-');
      if (sw) {
        setSwitchStatus(sw);
        setHint(`获取成功：当前${sw}`);
        message.success(`当前闸位：${sw}`);
      } else {
        setHint('抄表成功，但回包未带闸位字段');
        message.warning('抄表成功，但未能解析开关闸状态');
      }
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '获取失败';
      setError(errMsg);
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const meterLabel =
    device?.current_meter_no || device?.factory_meter_no || device?.device_mac || device?.id || '';

  return (
    <Modal
      title={`${isBYElectric ? 'BY ' : ''}开关闸${meterLabel ? ` · ${meterLabel}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={isBYElectric ? 560 : 480}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={
            isBYElectric
              ? 'BY 远程：开闸 / 关闸 / 获取状态（抄表读闸位）。接口待接；操作会写入下方完整返回区。'
              : '点「获取开关闸状态」会远程抄表读取实时闸位与电量；开/关闸后可再点一次核对。'
          }
        />

        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="当前闸位">
            <Text strong style={{ fontSize: 16 }}>{switchStatus || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="剩余电量">{remainText}</Descriptions.Item>
          <Descriptions.Item label="总用电量">{totalText}</Descriptions.Item>
        </Descriptions>

        <Space wrap>
          <Button type="primary" loading={loading} onClick={handleQueryStatus}>
            {isBYElectric ? '获取开关闸状态（抄表）' : '获取开关闸状态'}
          </Button>
          <Button loading={loading} onClick={() => publishCmd('open')}>
            开闸
          </Button>
          <Button danger loading={loading} onClick={() => publishCmd('close')}>
            关闸
          </Button>
        </Space>

        <div style={{ minHeight: 40 }}>
          {error ? (
            <Alert type="error" showIcon message={error} />
          ) : hint ? (
            <Alert type="success" showIcon message={hint} />
          ) : null}
        </div>

        {isBYElectric && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>完整返回（厂家 JSON）</Text>
              <Button
                size="small"
                onClick={async () => {
                  const t = String(byVendorLog || '').trim();
                  if (!t) {
                    message.warning('暂无返回内容');
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(t);
                    message.success('已复制');
                  } catch (e) {
                    message.error('复制失败');
                  }
                }}
              >
                复制
              </Button>
            </div>
            <TextArea
              value={byVendorLog}
              onChange={(e) => setByVendorLog(e.target.value)}
              placeholder="点击上方操作后，预览/真实厂家返回会出现在这里…"
              autoSize={{ minRows: 6, maxRows: 14 }}
              style={{ marginTop: 8, fontFamily: 'Consolas, Monaco, monospace', fontSize: 12 }}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default MeterSwitchModal;
